import React, { useState, useCallback } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet-async";
import {
  parsePhoneNumberFromString,
  getCountryCallingCode,
} from "libphonenumber-js";
import { useLoginMutation } from "../../services/apiSlice";
import Button from "../../components/common/Button/Button";
import { CheckboxCommon } from "../../components/common/CheckBox/CheckboxCommon";
import images from "../../images";
import CustomInputProps from "../../components/CustomInputProps/CustomInputProps";

// Constants
const DEFAULT_COUNTRY_CODE = "+65";
const FROM_QRCODE = "qrcode";
const TOAST_MESSAGES = {
  OTP_SUCCESS: "toast.success.otpCodeWillExprireIn5Minutes",
  LOGIN_FAILED: "toast.error.loginFailed",
  INVALID_PHONE: "signIn.invalidPhoneNumber",
};

// Utility Functions
const parseQueryParams = (search) => {
  const params = new URLSearchParams(search);
  return {
    questId: params.get("quest_id"),
    locationId: params.get("location_id"),
    isFromQRCode: params.get("from") === FROM_QRCODE,
  };
};

const parsePhoneNumber = (phone) => {
  try {
    const phoneFirst8 = phone.slice(0, 8);

    const phoneWithCountryCode = phoneFirst8.startsWith("+")
      ? phoneFirst8
      : `${DEFAULT_COUNTRY_CODE}${phoneFirst8}`;

    const phoneNumber = parsePhoneNumberFromString(phoneWithCountryCode);
    if (phoneNumber && phoneNumber.isValid()) {
      const country = phoneNumber.country;
      const countryCode = country ? `+${getCountryCallingCode(country)}` : "";
      // Trả về 8 số đầu tiên (bỏ country code)
      const phoneWithoutCountryCode = phoneFirst8;
      return { countryCode, phoneWithoutCountryCode, isValid: true };
    }
    return { countryCode: "", phoneWithoutCountryCode: "", isValid: false };
  } catch {
    return { countryCode: "", phoneWithoutCountryCode: "", isValid: false };
  }
};

const handleApiError = (error, t) => {
  const status = error?.status || error?.response?.status;

  if (status === 400) {
    return t("toast.error.verifyingOtp");
  }

  if (status === 422) {
    return (
      <span>
        {t("signIn.yourNumberIsNotRegistered")}{" "}
        <Link
          to="/register"
          className="text-[#EF4056] font-bold transition-colors"
        >
          {t("signUp.signUp")}
        </Link>
      </span>
    );
  }

  const fallbackMsg =
    error?.data?.detail?.[0]?.msg ||
    error?.response?.data?.detail?.[0]?.msg ||
    t(TOAST_MESSAGES.LOGIN_FAILED);

  return fallbackMsg;
};

// Components
const SignInHeader = () => {
  const { t } = useTranslation();
  return (
    <header className="">
      <h1 className="text-2xl font-bold text-[#253FF6]">{t("signIn.title")}</h1>
    </header>
  );
};

const SignInForm = ({
  signInData,
  setSignInData,
  rememberMe,
  setRememberMe,
  onSubmit,
  questId,
  locationId,
  isFromQRCode,
  error,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handlePhoneChange = useCallback(
    (value) => {
      const rawPhone =
        typeof value === "string" ? value.replace(/[^0-9]/g, "") : "";

      const phoneFirst8Digits = rawPhone.slice(0, 8);

      const formattedPhone =
        phoneFirst8Digits.length > 4
          ? `${phoneFirst8Digits.slice(0, 4)}-${phoneFirst8Digits.slice(4, 8)}`
          : phoneFirst8Digits;

      const parsed = parsePhoneNumber(phoneFirst8Digits);
      setSignInData({
        phoneNumber: formattedPhone,
        rawPhoneNumber: phoneFirst8Digits,
        countryCode: parsed.countryCode,
        phoneWithoutCountryCode: parsed.phoneWithoutCountryCode,
        isPhoneValid: parsed.isValid,
      });
    },
    [setSignInData]
  );

  return (
    <div className="flex flex-col items-center mt-4 max-w-[340px] text-sm text-[#A0A0A0] gap-6">
      <label htmlFor="phone-input" className="sr-only">
        {t("signIn.phoneNumber")}
      </label>
      <div className="w-full">
        {/* <PhoneInputProps
          value={signInData.phoneNumber}
          onChange={handlePhoneChange}
        /> */}
        <CustomInputProps
          value={signInData.phoneNumber}
          onChange={handlePhoneChange}
          placeholder={t("signUp.whatsAppNumber")}
          iconImage={images.WhatsappIconWhite}
          countryCode={signInData.countryCode || DEFAULT_COUNTRY_CODE}
          iconDropdown={images.DropPhoneIconGray}
        />
        {error.phone && (
          <p className="text-[#EF4056] text-sm  mt-2">{error.phone}</p>
        )}
      </div>

      <label className="flex items-center gap-2.5 w-full cursor-pointer">
        <CheckboxCommon
          className="rounded-[2px] w-4 h-4 bg-white border-2 border-[#A0A0A0] data-[state=checked]:bg-white data-[state=checked]:text-[#A0A0A0]"
          checked={rememberMe}
          onCheckedChange={setRememberMe}
          id="remember-me"
          aria-label={t("signIn.rememberMe")}
        />
        <span className="text-[#A0A0A0] text-sm font-medium">
          {t("signIn.rememberMe")}
        </span>
      </label>

      <Button
        className="px-[18px] py-3 text-[20px] font-medium text-white bg-[#253FF6] rounded-md max-w-[368px] w-full h-[50px] mt-0"
        text={t("signUp.getWhatsAppOTP")}
        iconSub={images.WhatsappIconWhite}
        onClick={onSubmit}
        disabled={!signInData.isPhoneValid}
        aria-label={t("signUp.getWhatsAppOTP")}
      />
      <div className="flex justify-start items-center  w-full font-medium gap-1 ">
        <span className="text-[#A0A0A0]">{t("signIn.noAccount")}</span>{" "}
        <button
          onClick={() =>
            navigate(
              `/register?${
                isFromQRCode
                  ? `from=${FROM_QRCODE}&quest_id=${questId}&location_id=${locationId}`
                  : ""
              }`
            )
          }
          className="text-[#253FF6] font-bold hover:underline"
          aria-label={t("signUp.signUp")}
        >
          {t("signUp.signUp")}
        </button>
      </div>
    </div>
  );
};

const SignInFooter = () => null;

// Main Component
const SignInPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [login] = useLoginMutation();

  const [error, setError] = useState({
    phone: "",
    isPhoneValid: false,
  });
  const [signInData, setSignInData] = useState({
    phoneNumber: "",
    rawPhoneNumber: "",
    countryCode: "+65",
    phoneWithoutCountryCode: "",
    isPhoneValid: false,
  });
  const [rememberMe, setRememberMe] = useState(false);
  const { questId, locationId, isFromQRCode } = parseQueryParams(
    location.search
  );

  // const validateForm = (signInData, t) => {
  //   const errors = {};

  //   if (!signInData.isPhoneValid) {
  //     errors.phone = t(TOAST_MESSAGES.INVALID_PHONE);
  //   }

  //   return errors;
  // };

  const handleSubmit = useCallback(() => {
    setError({
      phone: "",
      privacyPolicy: "",
    });

    const body = {
      phone: signInData.rawPhoneNumber,
      country_code: signInData.countryCode,
    };

    login(body)
      .unwrap()
      .then((response) => {
        if (response.status === 200) {
          toast.success(t(TOAST_MESSAGES.OTP_SUCCESS));
          navigate(
            `/verify-otp?phone=${signInData.phoneWithoutCountryCode}&country_code=${signInData.countryCode}&quest_id=${questId}&location_id=${locationId}&remember_me=${rememberMe}`
          );
        }
      })
      .catch((error) => {
        setError({
          phone: handleApiError(error, t),
        });
      });
  }, [signInData, rememberMe, questId, locationId, login, navigate, t]);

  return (
    <div className="flex  flex-col justify-evenly px-11 w-full bg-white h-full ">
      <Helmet>
        <meta name="theme-color" content="#fefce8" />
      </Helmet>
      <div className="flex items-center w-full pt-[155px]">
        <img
          src={images.LogoLogin}
          className="w-[110px] h-[81.95px] aspect-[110/81.95]"
          alt=""
        />
      </div>
      <div className="flex flex-col gap-6 justify-between mt-[60px]  w-full bg-white ">
        <div className="w-full">
          <SignInHeader />
          <SignInForm
            signInData={signInData}
            setSignInData={setSignInData}
            rememberMe={rememberMe}
            setRememberMe={setRememberMe}
            onSubmit={handleSubmit}
            questId={questId}
            locationId={locationId}
            isFromQRCode={isFromQRCode}
            error={error}
          />
        </div>
        <SignInFooter />
      </div>
      <div></div>
      <div></div>
      <div></div>
    </div>
  );
};

export default SignInPage;
